// =============================================================================
// app/src/paginas/FiadoPorPessoa.tsx — o fiado com UMA LINHA POR PESSOA.
//
// Pedido do dono (26/09/2026): "uma tela separada em fiado onde eu consigo
// visualizar os fiados somente por pessoa e, quando clico, ele abre com as
// dívidas" — mais o prazo médio da dívida e as outras estatísticas.
//
// Quem é a mesma pessoa, quanto ela deve e os números: lib/fiado.ts, testado.
// Aqui é só a tela. As vendas dentro da pessoa são o MESMO cartão da visão por
// venda (FiadoConta.tsx), com as mesmas ações: receber, devolver, desconto.
// =============================================================================
import { Fragment, useId, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import Aviso from '../componentes/Aviso';
import CampoBusca from '../componentes/CampoBusca';
import Carregando from '../componentes/Carregando';
import CartaoKPI from '../componentes/CartaoKPI';
import Ordenacao from '../componentes/Ordenacao';
import { acertosDoFiado, listarFiados } from '../lib/consultas';
import {
  DIAS_DA_JANELA, DIAS_PARA_ATRASO, ORDENS_PESSOAS, estaAberta, ordenarPessoas, pessoasDoFiado,
  resumoDoFiado, textoDias, textoPorcento,
  type OrdemPessoas, type PessoaDoFiado, type ResumoDoFiado,
} from '../lib/fiado';
import { brl, dataBR, dataSoDiaBR, hojeISO } from '../lib/formato';
import { casaBusca } from '../lib/listas';
import { mensagemDeErro } from '../lib/supabase';
import { CartaoDoFiado, useRecarregarFiado } from './FiadoConta';
import { EmitirFiadoDaPessoa } from '../componentes/EmitirFiado';
import { podeVerFaturamento, useSessao } from '../lib/sessao';

const plural = (n: number, um: string, varios: string): string => (n === 1 ? um : varios);

export default function FiadoPorPessoa() {
  // TODAS as contas, quitadas inclusive: "quanto já comprou", "quanto já
  // pagou" e o prazo médio vivem do histórico, não só do que está aberto. A
  // chave é a da visão por venda com "mostrar quitados" — um cache só.
  const fiados = useQuery({ queryKey: ['fiados', true], queryFn: () => listarFiados(true) });
  const acertos = useQuery({ queryKey: ['fiado-acertos'], queryFn: acertosDoFiado });
  const recarregar = useRecarregarFiado();

  const [busca, setBusca] = useState('');
  const [ordem, setOrdem] = useState<OrdemPessoas>('maior');
  const [soAtrasados, setSoAtrasados] = useState(false);
  const [comQuitados, setComQuitados] = useState(false);
  // Várias abertas ao mesmo tempo: comparar duas oficinas é uso de verdade. É
  // pela CHAVE da pessoa, e não pela posição, que a linha continua aberta
  // depois que um acerto reordena a lista.
  const [abertas, setAbertas] = useState<ReadonlySet<string>>(() => new Set());

  const hoje = hojeISO();
  const pessoas = useMemo(
    () => pessoasDoFiado(fiados.data ?? [], acertos.data ?? [], hoje),
    [fiados.data, acertos.data, hoje],
  );
  // Os números são da LOJA INTEIRA e não seguem o filtro — ao contrário dos
  // cartões da visão por venda. Seguir o "esconder quem já quitou" tiraria do
  // prazo médio justamente quem paga em dia, e o número mentiria para pior.
  const resumo = useMemo(
    () => resumoDoFiado(pessoas, acertos.data ?? [], hoje),
    [pessoas, acertos.data, hoje],
  );

  const lista = useMemo(() => ordenarPessoas(pessoas.filter((p) => (
    (comQuitados || p.saldo > 0)
    && (!soAtrasados || p.atrasadas > 0)
    // Todas as grafias da pessoa entram na busca: quem digita "bixao" acha a
    // linha mesmo que o nome escolhido para ela seja o do cadastro.
    && casaBusca(busca, ...new Set(p.contas.map((f) => f.cliente)))
  )), ordem), [pessoas, comQuitados, soAtrasados, busca, ordem]);

  const alternar = (chave: string) => setAbertas((atual) => {
    const nova = new Set(atual);
    if (nova.has(chave)) nova.delete(chave); else nova.add(chave);
    return nova;
  });

  if (fiados.isPending) return <Carregando />;
  if (fiados.error) return <Aviso tipo="erro">{mensagemDeErro(fiados.error)}</Aviso>;

  const filtrando = busca.trim() !== '' || soAtrasados;

  return (
    <div className="space-y-4">
      <NumerosDoFiado resumo={resumo}
                      acertos={acertos.isPending ? 'carregando' : acertos.error ? 'erro' : 'ok'} />
      {acertos.error ? (
        <Aviso tipo="erro">
          Os pagamentos não carregaram — o prazo médio, o recebido em {DIAS_DA_JANELA} dias e
          o último pagamento ficaram de fora. {mensagemDeErro(acertos.error)}
        </Aviso>
      ) : null}

      <div className="cartao space-y-3 p-3">
        <CampoBusca valor={busca} aoMudar={setBusca} placeholder="Buscar pelo nome" />
        <div className="flex flex-wrap items-center justify-between gap-3">
          <Ordenacao opcoes={ORDENS_PESSOAS} valor={ordem} aoMudar={setOrdem} />
          <p className="text-xs text-dim">
            {lista.length} {plural(lista.length, 'pessoa', 'pessoas')}
          </p>
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={soAtrasados}
                 onChange={(e) => setSoAtrasados(e.target.checked)} />
          Só quem tem dívida atrasada
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={comQuitados}
                 onChange={(e) => setComQuitados(e.target.checked)} />
          Mostrar também quem já quitou tudo
        </label>
      </div>

      <ul className="space-y-2">
        {lista.map((p) => (
          <li key={p.chave}>
            <LinhaDaPessoa pessoa={p} hoje={hoje} aberta={abertas.has(p.chave)}
                           aoAlternar={() => alternar(p.chave)} aoMudar={recarregar} />
          </li>
        ))}
        {lista.length === 0 && (
          <li className="text-sm text-dim">
            {filtrando ? 'Ninguém casa com esses filtros.' : 'Ninguém devendo. 👏'}
          </li>
        )}
      </ul>
    </div>
  );
}

/** Os números da cobrança, da loja inteira. Exportada para o teste de tela. */
export function NumerosDoFiado({ resumo: r, acertos }: {
  resumo: ResumoDoFiado;
  acertos: 'carregando' | 'ok' | 'erro';
}) {
  // O que depende dos acertos diz "…" enquanto eles vêm e "—" se não vieram.
  // Nunca R$ 0,00: sem os pagamentos na mão, zero seria afirmar que ninguém
  // pagou nada.
  const semAcerto = acertos === 'carregando' ? '…' : '—';
  const rotuloMaiores = r.maiores.quantas <= 1 ? 'Maior devedor' : `${r.maiores.quantas} maiores devedores`;

  return (
    <section className="space-y-2" aria-label="Números do fiado">
      <p className="rotulo">Todo o fiado da loja</p>
      {/* 2 colunas até a tela larga: com 4 lado a lado em 640 px, um valor
          como "R$ 13.026,50" em letra grande passa da borda do cartão. */}
      <div className="grid grid-cols-2 gap-2 sm:gap-3 lg:grid-cols-4">
        <CartaoKPI rotulo="Total a receber" valor={brl(r.totalAberto)}
                   detalhe={`${r.pessoasDevendo} ${plural(r.pessoasDevendo, 'pessoa', 'pessoas')} · `
                     + `${r.contasAbertas} ${plural(r.contasAbertas, 'venda', 'vendas')}`} />
        {/* Rótulos CURTOS: no celular, "Prazo médio para pagar" quebrava em
            duas linhas e desalinhava o número do cartão vizinho. O que o
            número quer dizer vai no detalhe. */}
        <CartaoKPI rotulo="Prazo médio"
                   valor={r.prazoMedio === null ? semAcerto : textoDias(r.prazoMedio)}
                   detalhe="para pagar, contando da compra" />
        <CartaoKPI rotulo="Idade média"
                   valor={r.idadeMedia === null ? '—' : textoDias(r.idadeMedia)}
                   detalhe="da dívida em aberto hoje"
                   tom={(r.idadeMedia ?? 0) > DIAS_PARA_ATRASO ? 'alerta' : 'neutro'} />
        <CartaoKPI rotulo="Atrasado" valor={brl(r.atrasado)}
                   detalhe={r.atrasado > 0
                     ? `${r.pessoasAtrasadas} ${plural(r.pessoasAtrasadas, 'pessoa', 'pessoas')} · `
                       + `${textoPorcento(r.atrasado, r.totalAberto)} do total`
                     : `nada passou de ${DIAS_PARA_ATRASO} dias`}
                   tom={r.atrasado > 0 ? 'alerta' : 'neutro'} />
      </div>

      <div className="cartao space-y-2 p-3">
        <p className="rotulo">Há quanto tempo o dinheiro está na rua</p>
        {/* UMA grade para as quatro faixas, e não uma por linha: com a coluna
            do valor medida linha a linha, "R$ 0,00" deixava a barra de baixo
            mais comprida que as outras — e barra só se compara com barra do
            mesmo tamanho. */}
        <div className="grid grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-x-2 gap-y-1.5 text-xs">
          {r.faixas.map((f) => {
            const fracao = r.totalAberto > 0 ? f.valor / r.totalAberto : 0;
            return (
              <Fragment key={f.rotulo}>
                <span className={f.passouDoCostume && f.valor > 0 ? 'font-bold text-alerta' : 'text-mute'}>
                  {f.rotulo}
                </span>
                <span className="block h-2 bg-edge" aria-hidden="true">
                  <span className={`block h-full ${f.passouDoCostume ? 'bg-alerta' : 'bg-mute'}`}
                        style={{ width: `${Math.round(fracao * 1000) / 10}%` }} />
                </span>
                <span className="text-right tabular-nums">
                  {brl(f.valor)}
                  <span className="text-dim"> · {textoPorcento(f.valor, r.totalAberto)}</span>
                </span>
              </Fragment>
            );
          })}
        </div>
        <p className="text-xs text-dim">
          Em amarelo, o que passou de {DIAS_PARA_ATRASO} dias — sem data combinada, já é atraso.
        </p>
      </div>

      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 sm:gap-3">
        <CartaoKPI rotulo={`Recebido em ${DIAS_DA_JANELA} dias`}
                   valor={acertos === 'ok' ? brl(r.recebido30) : semAcerto}
                   detalhe={`saiu em fiado: ${brl(r.vendido30)}`} />
        <CartaoKPI rotulo={rotuloMaiores}
                   valor={r.maiores.quantas > 0 ? textoPorcento(r.maiores.valor, r.totalAberto) : '—'}
                   detalhe={r.maiores.quantas > 0 ? `${brl(r.maiores.valor)} do total` : undefined} />
        <CartaoKPI rotulo="Dado como perdido" valor={brl(r.perdido)}
                   detalhe={`${textoPorcento(r.perdido, r.vendidoFiado)} do vendido fiado`}
                   tom={r.perdido > 0 ? 'erro' : 'neutro'}
                   className="col-span-2 sm:col-span-1" />
      </div>
    </section>
  );
}

/** O que a linha fechada diz, abaixo do nome. */
function resumoDaLinha(p: PessoaDoFiado): string {
  const partes: string[] = [];
  if (p.abertas.length === 1) {
    partes.push(`1 venda em aberto há ${textoDias(p.diasMaisAntiga ?? 0)}`);
  } else if (p.abertas.length > 1) {
    partes.push(`${p.abertas.length} vendas em aberto`);
    if (p.diasMaisAntiga !== null) partes.push(`a mais antiga há ${textoDias(p.diasMaisAntiga)}`);
  } else {
    partes.push(p.perdido > 0 ? `dívida dada como perdida (${brl(p.perdido)})` : 'quitou tudo');
  }
  if (p.prazoMedio !== null) partes.push(`costuma pagar em ${textoDias(p.prazoMedio)}`);
  return partes.join(' · ');
}

/** A linha da pessoa: fechada, o total; tocada, abre com as dívidas.
 *  Exportada para o teste de tela (FiadoPorPessoa.test.tsx). */
export function LinhaDaPessoa({ pessoa: p, hoje, aberta, aoAlternar, aoMudar }: {
  pessoa: PessoaDoFiado;
  hoje: string;
  aberta: boolean;
  aoAlternar: () => void;
  aoMudar: () => void;
}) {
  const id = useId();
  const deve = p.saldo > 0;
  return (
    <div className={`cartao ${p.atrasadas > 0 ? 'border-alerta/50' : ''}`}>
      {/* A linha INTEIRA é o botão da sanfona, como na aba Vendas: no
          celular, tocar no nome ou no valor tem de dar no mesmo. */}
      <button type="button" className="flex w-full items-start justify-between gap-3 p-3 text-left"
              aria-expanded={aberta} aria-controls={id} onClick={aoAlternar}>
        <span className="min-w-0">
          <span className="block text-sm font-bold">
            <span className="text-dim">{aberta ? '▾' : '▸'} </span>{p.nome}
          </span>
          <span className="block text-xs text-mute">{resumoDaLinha(p)}</span>
          {p.atrasadas > 0 && (
            <span className="block text-xs font-bold text-alerta">
              {brl(p.saldoAtrasado)} atrasado
              {p.atrasadas > 1 ? ` em ${p.atrasadas} vendas` : ''}
            </span>
          )}
        </span>
        <span className="shrink-0 text-right">
          <span className={`block text-lg font-black tabular-nums ${deve ? '' : 'text-dim'}`}>
            {brl(p.saldo)}
          </span>
          <span className="block text-xs text-dim">
            {aberta ? 'fechar ▴' : deve ? 'ver dívidas ▾' : 'ver histórico ▾'}
          </span>
        </span>
      </button>
      {aberta && <DetalheDaPessoa id={id} pessoa={p} hoje={hoje} aoMudar={aoMudar} />}
    </div>
  );
}

/** Um número pequeno do detalhe da pessoa. */
function Numero({ rotulo, valor, detalhe, erro = false }: {
  rotulo: string; valor: string; detalhe?: string; erro?: boolean;
}) {
  return (
    <div className="min-w-0">
      <dt className="rotulo">{rotulo}</dt>
      <dd className={`text-sm font-bold tabular-nums ${erro ? 'text-erro' : ''}`}>{valor}</dd>
      {detalhe ? <dd className="text-xs text-dim">{detalhe}</dd> : null}
    </div>
  );
}

/** A pessoa aberta: o histórico dela em números e as dívidas, uma a uma. */
function DetalheDaPessoa({ id, pessoa: p, hoje, aoMudar }: {
  id: string; pessoa: PessoaDoFiado; hoje: string; aoMudar: () => void;
}) {
  const [verFechadas, setVerFechadas] = useState(false);
  // EMITIR (14/10/2026): o extrato da pessoa — imprimir na loja e assinar
  // junto, ou o PDF para mandar à oficina. O mesmo painel da aba Vendas.
  const [emitindo, setEmitindo] = useState(false);
  const { papel, perfil } = useSessao();
  const fechadas = p.contas.filter((f) => !estaAberta(f));
  const primeira = p.contas[0];

  return (
    <div id={id} className="space-y-3 border-t border-edge p-3">
      <dl className="grid grid-cols-2 gap-x-4 gap-y-3 sm:grid-cols-4">
        <Numero rotulo="Comprou fiado" valor={brl(p.comprado)}
                detalhe={`${p.contas.length} ${plural(p.contas.length, 'venda', 'vendas')}`
                  + (primeira ? ` desde ${dataBR(primeira.vendida_em)}` : '')} />
        <Numero rotulo="Já pagou" valor={brl(p.pago)}
                detalhe={p.pagamentos > 0
                  ? `em ${p.pagamentos} ${plural(p.pagamentos, 'pagamento', 'pagamentos')}`
                  : 'nenhum pagamento ainda'} />
        <Numero rotulo="Costuma pagar em"
                valor={p.prazoMedio === null ? '—' : textoDias(p.prazoMedio)}
                detalhe="da compra até o pagamento" />
        <Numero rotulo="Último pagamento"
                valor={p.ultimoPagamento ? dataSoDiaBR(p.ultimoPagamento.dia) : '—'}
                detalhe={p.ultimoPagamento ? brl(p.ultimoPagamento.valor) : undefined} />
        {/* Cada abatimento no seu número, como no cartão da venda: desconto é
            dinheiro de que a loja abriu mão; devolução é peça que voltou. */}
        {p.desconto > 0 && <Numero rotulo="Descontos dados" valor={brl(p.desconto)} />}
        {p.devolvido > 0 && <Numero rotulo="Peças devolvidas" valor={brl(p.devolvido)} />}
        {p.perdido > 0 && <Numero rotulo="Dado como perdido" valor={brl(p.perdido)} erro />}
      </dl>

      {emitindo ? (
        <EmitirFiadoDaPessoa
          nome={p.nome}
          contas={p.contas}
          verFaturamento={podeVerFaturamento(papel)}
          emitidoPor={perfil?.nome ?? null}
          aoFechar={() => setEmitindo(false)}
        />
      ) : (
        <button type="button" className="botao-primario w-full py-3" onClick={() => setEmitindo(true)}>
          Emitir — imprimir ou salvar PDF
        </button>
      )}

      {p.abertas.length > 0 ? (
        <ul className="space-y-2">
          {p.abertas.map((f) => (
            <li key={f.id}>
              <CartaoDoFiado f={f} hoje={hoje} aoMudar={aoMudar} dentroDaPessoa />
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-xs text-dim">Nenhuma dívida em aberto.</p>
      )}

      {fechadas.length > 0 && (
        <div className="space-y-2">
          <button type="button" className="text-xs text-dim underline" aria-expanded={verFechadas}
                  onClick={() => setVerFechadas((v) => !v)}>
            {verFechadas ? 'esconder' : 'ver'}{' '}
            {fechadas.length === 1 ? 'a venda já fechada' : `as ${fechadas.length} vendas já fechadas`}
            {p.perdido > 0 ? ' (quitadas ou perdidas)' : ' (quitadas)'}
          </button>
          {verFechadas && (
            <ul className="space-y-2">
              {fechadas.map((f) => (
                <li key={f.id}>
                  <CartaoDoFiado f={f} hoje={hoje} aoMudar={aoMudar} dentroDaPessoa />
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
